import { useState } from "react"
import { ArrowRight } from "@untitledui/icons"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import {
  activationApi,
  type ActivationView,
  type BusinessDetails,
} from "@/lib/api/onboarding-activation"
import {
  PrimaryButton,
  StageHeader,
  Field,
  AInput,
  ActionRow,
  focusFirstInvalid,
  sectionLabelClass,
  EMAIL_RE,
} from "./ui"

// Step 3 — the shop's IT contact plus where invoices go. The last of the
// three intake steps: Continue saves the WHOLE intake draft (shop + systems +
// contacts) in one server call, so a refresh resumes cleanly at the start of
// the intake or at the signature — never in between.
export function ItContactStep({
  token,
  draft,
  onBack,
  onSaved,
}: {
  token: string
  draft: BusinessDetails
  onBack: () => void
  onSaved: (next: ActivationView) => void
}) {
  const [first, setFirst] = useState(draft.itContactFirstName ?? "")
  const [last, setLast] = useState(draft.itContactLastName ?? "")
  const [email, setEmail] = useState(draft.itContactEmail ?? "")
  const [phone, setPhone] = useState(draft.itContactPhone ?? "")
  const [billingEmail, setBillingEmail] = useState(draft.billingEmail ?? "")
  const [tried, setTried] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const errors = {
    first: !first.trim() && "Enter a first name",
    last: !last.trim() && "Enter a last name",
    email: !email.trim()
      ? "Enter an email"
      : !EMAIL_RE.test(email.trim()) && "That email doesn't look right",
    phone: !phone.trim() && "Enter a phone number",
    billingEmail: !billingEmail.trim()
      ? "Enter the email for invoices"
      : !EMAIL_RE.test(billingEmail.trim()) && "That email doesn't look right",
  }
  const missing = Object.values(errors).filter(Boolean).length

  const save = async () => {
    if (missing > 0) {
      setTried(true)
      focusFirstInvalid()
      return
    }
    if (submitting) return
    setSubmitting(true)
    try {
      onSaved(
        await activationApi.saveDetails(token, {
          ...draft,
          itContactFirstName: first.trim(),
          itContactLastName: last.trim(),
          itContactEmail: email.trim(),
          itContactPhone: phone.trim(),
          billingEmail: billingEmail.trim(),
        }),
      )
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Your details could not be saved.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <StageHeader
        stage="Step 3 of 5"
        title="Your IT & billing contacts"
        lede="Enter the person we should contact about the data connection, and the email where invoices should go."
      />

      <div className="space-y-4">
        <p className={sectionLabelClass}>IT contact — if that's you, enter your own details</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" error={tried && errors.first}>
            <AInput
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              autoComplete="given-name"
              valid={!errors.first}
              invalid={Boolean(tried && errors.first)}
            />
          </Field>
          <Field label="Last name" error={tried && errors.last}>
            <AInput
              value={last}
              onChange={(e) => setLast(e.target.value)}
              autoComplete="family-name"
              valid={!errors.last}
              invalid={Boolean(tried && errors.last)}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" error={tried && errors.email}>
            <AInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoComplete="email"
              placeholder="it@yourshop.com"
              valid={!errors.email}
              invalid={Boolean(tried && errors.email)}
            />
          </Field>
          <Field label="Phone" error={tried && errors.phone}>
            <AInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              valid={!errors.phone}
              invalid={Boolean(tried && errors.phone)}
            />
          </Field>
        </div>

        <p className={`pt-2 ${sectionLabelClass}`}>Billing</p>
        <Field
          label="Billing email"
          hint="Monthly invoices are sent here"
          error={tried && errors.billingEmail}
        >
          <AInput
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            inputMode="email"
            placeholder="billing@yourshop.com"
            valid={!errors.billingEmail}
            invalid={Boolean(tried && errors.billingEmail)}
          />
        </Field>
      </div>

      <ActionRow
        back={onBack}
        note={
          tried && missing > 0
            ? `${missing} field${missing > 1 ? "s" : ""} to go — they're marked above`
            : undefined
        }
      >
        <PrimaryButton onClick={save} loading={submitting}>
          Save and continue
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </ActionRow>
    </div>
  )
}
